import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, CheckCircle2, Receipt, Network, FileCheck, Search, Layers, Copy, Stethoscope, FileText, Clock, ShieldCheck, TrendingDown, Sparkles } from 'lucide-react';
const capabilities = [{
  icon: Receipt,
  name: 'State Fee Schedule Application',
  description: 'Accurate application of state-mandated fee schedules across every jurisdiction in the country.'
}, {
  icon: Network,
  name: 'PPO Network Pricing',
  description: 'Access to leading national and regional PPO networks for additional negotiated discounts beyond fee schedules.'
}, {
  icon: Search,
  name: 'Usual & Customary Review',
  description: "Where fee schedules don't apply, U&C analysis ensures fair pricing based on regional market data."
}, {
  icon: FileCheck,
  name: 'Code Editing & Compliance',
  description: 'Automated and clinical review for proper CPT, HCPCS, and ICD-10 coding, modifiers, and billing rules.'
}, {
  icon: Copy,
  name: 'Duplicate Detection',
  description: 'Cross-bill and cross-claim duplicate identification — catching what manual review misses.'
}, {
  icon: Layers,
  name: 'Unbundling & Upcoding',
  description: 'Clinical edits to identify unbundled procedures, upcoded services, and improper bundling violations.'
}, {
  icon: Stethoscope,
  name: 'Specialty Bill Review',
  description: 'Expert review of complex bills: hospital, surgical, anesthesia, durable medical equipment, and pharmacy.'
}, {
  icon: FileText,
  name: 'Explanation of Review (EOR)',
  description: 'Clear, defensible EOR documentation for every bill — supporting providers, adjusters, and regulators.'
}];
const benefits = [{
  icon: Clock,
  title: 'Fast turnaround',
  stat: '3 days',
  description: 'Standard bills reviewed and processed within three days — most within 24 to 48 hours.'
}, {
  icon: ShieldCheck,
  title: 'Compliance assured',
  stat: '50',
  description: "Compliant with workers' compensation billing regulations in all 50 states."
}];
export function MedicalBillReview() {
  return <div className="bg-brand-paper" data-id="element-670" data-mp="rvpe3l5tbdfg">
      {/* Hero */}
      <section className="relative bg-brand-navy pt-40 pb-24 overflow-hidden" data-id="element-671" data-mp="rxa9te3v52ew">
        <div className="absolute inset-0 opacity-10" data-id="element-672" data-mp="1pienig2ugsbc">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(#C9A24B 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} data-id="element-673" data-mp="1kk4tw9wrpzru"></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy/95 to-brand-navy/80" data-id="element-674" data-mp="1atii6qs69tef"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-675" data-mp="28bijw97g5j9n">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-676" data-mp="23h601y2265xd">
            <div className="inline-flex items-center space-x-2 bg-brand-gold/15 border border-brand-gold/40 rounded-full px-4 py-1.5 mb-6" data-id="element-677" data-mp="2kqqbt9elo5r7">
              <span className="text-xs font-bold tracking-widest text-brand-gold uppercase" data-id="element-678" data-mp="2uqh6pc72omrt">
                Managed Care Service
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium text-white leading-[1.1] mb-6 text-balance" data-id="element-679" data-mp="2ypq1mkdumqoy">
              Medical{' '}
              <span className="text-brand-gold italic" data-id="element-680" data-mp="38gko9pv0bsax">Bill Review</span>
            </h1>
            <p className="text-xl text-white/80 mb-10 leading-relaxed font-light max-w-2xl" data-id="element-681" data-mp="3dhi4vy0dceab">
              Smart technology and expert clinical review combine to ensure
              every medical bill is priced accurately, coded correctly, and
              compliant in every jurisdiction.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-682" data-mp="e3hk4t978ke4">
              <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold hover:bg-white transition-colors flex items-center" data-id="element-683" data-mp="srvo57icqew9">
                Request a Demo <ArrowRight className="w-5 h-5 ml-2" data-id="element-684" data-mp="3q46mz4gtxh" />
              </Link>
              <a href="tel:8552601053" className="flex items-center text-white border border-white/30 px-8 py-4 rounded font-semibold hover:bg-white/10 transition-colors" data-id="element-685" data-mp="3ra18qt3ccctk">
                <Phone className="w-5 h-5 mr-2" data-id="element-686" data-mp="518eohssfh7l" /> 855.260.1053
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-24 bg-white" data-id="element-687" data-mp="1ghpztlqxpxex">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center" data-id="element-688" data-mp="1ggn7d9dyrmww">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-689" data-mp="1nzed3kxha540">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-690" data-mp="26rgw32xldxhf">
              The Problem We Solve
            </h2>
            <h3 className="text-4xl font-serif text-brand-navy mb-6 leading-tight" data-id="element-691" data-mp="2bpqs1fr55243">
              Pay only what's right. Defend every dollar.
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-692" data-mp="21zz0yvi92r4d">
              Workers' compensation medical bills are complex, jurisdictional,
              and full of opportunity for overpayment. Without rigorous review,
              every claim leaks dollars — and creates compliance risk.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-693" data-mp="34bp9omt5sjh3">
              Our hybrid technology-plus-clinical-review program catches what
              others miss: improperly coded charges, duplicate bills, upcharges,
              and treatments outside the medical necessity envelope. Every
              reduction is defensible and documented.
            </p>
            <ul className="space-y-4" data-id="element-694" data-mp="2lefp32c3kzy9">
              {['Clinical review by experienced bill reviewers', 'Fee schedule + PPO + U&C layered savings', 'EOR documentation that holds up to dispute'].map(item => <li key={item} className="flex items-start" data-id="element-695" data-mp="27mhm798zc0aj">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold mr-3 flex-shrink-0 mt-0.5" data-id="element-696" data-mp="27o2ib3axzrai" />
                  <span className="text-gray-800 font-medium" data-id="element-697" data-mp="22mv86xlbug0o">{item}</span>
                </li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="relative" data-id="element-698" data-mp="d6j74ne56wp3">
            <div className="absolute -inset-4 bg-brand-gold/20 rounded-2xl transform rotate-2" data-id="element-699" data-mp="r3xy3wk1ncj6"></div>
            <img src="https://cdn.magicpatterns.com/uploads/qACASDT9KTEWJ9npSkNKBE/Billreview.png" alt="Medical professionals reviewing bills and documentation together" className="relative rounded-2xl shadow-2xl h-[560px] w-full object-cover" data-id="element-700" data-mp="3qjciegzvhohy" />
          </motion.div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 bg-brand-paper" data-id="element-701" data-mp="37ufnqc0kctz7">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-702" data-mp="3bvrggqeqhvbm">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-703" data-mp="3bu6p19npushz">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-704" data-mp="259qgmo3s82ci">
              What's Included
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy mb-6" data-id="element-705" data-mp="20c8cdb0pmbau">
              Every layer of bill review.
            </h3>
            <p className="text-lg text-gray-600" data-id="element-706" data-mp="2a70dgoti3gad">
              From simple physician visits to complex hospital and surgical
              bills — every line item gets the right level of scrutiny.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-707" data-mp="2j8gcy1is1xzt">
            {capabilities.map((cap, i) => {
            const Icon = cap.icon;
            return <motion.div key={cap.name} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-xl transition-shadow border border-gray-100 group" data-id="element-708" data-mp="2szuiapxets0v" data-mpi={i}>
                  <div className="w-12 h-12 rounded-lg bg-brand-navy/5 text-brand-navy flex items-center justify-center mb-5 group-hover:bg-brand-gold group-hover:text-white transition-colors" data-id="element-709" data-mp="3hliq298ilytq">
                    <Icon className="w-6 h-6" data-id="element-710" data-mp="37nbbfx1p7suw" />
                  </div>
                  <h4 className="font-serif font-bold text-lg text-brand-navy mb-2" data-id="element-711" data-mp="2tv4ui8qq1hue">
                    {cap.name}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-712" data-mp="2xt2kvew8a9zd">
                    {cap.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-white" data-id="element-713" data-mp="2541r1eb06mo8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-714" data-mp="2577giunjnqkh">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-715" data-mp="297zrptfz85je">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-716" data-mp="1v76xl9tl7cgt">
              Outcomes
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy" data-id="element-717" data-mp="2069xygymguig">
              Built for measurable savings.
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-718" data-mp="2suxfq2k5x1r9">
            {benefits.map((b, i) => {
            const Icon = b.icon;
            return <motion.div key={b.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-brand-navy rounded-xl p-8 text-white relative overflow-hidden" data-id="element-719" data-mp="2e3osg03qfsha" data-mpi={i}>
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-brand-gold/10 rounded-full blur-2xl" data-id="element-720" data-mp="1902txvrfd1ck"></div>
                  <Icon className="w-8 h-8 text-brand-gold mb-6" data-id="element-721" data-mp="h6siwdfe14di" />
                  <div className="text-5xl font-serif font-bold text-brand-gold mb-2" data-id="element-722" data-mp="h96ecndzcill">
                    {b.stat}
                  </div>
                  <div className="font-bold text-white mb-3" data-id="element-723" data-mp="q89xtsh2700m">{b.title}</div>
                  <p className="text-sm text-white/70 leading-relaxed" data-id="element-724" data-mp="l8nk7hxbjhra">
                    {b.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-navy py-20 relative overflow-hidden" data-id="element-725" data-mp="1wmsu0rk7qluj">
        <div className="absolute top-0 left-0 w-full h-1 bg-brand-gold" data-id="element-726" data-mp="uaxwhrm6mkia"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-727" data-mp="z9hitt4otyb6">
          <h2 className="text-4xl md:text-5xl font-serif text-white mb-6 leading-tight" data-id="element-728" data-mp="zbc5pczis0hw">
            See your savings opportunity.
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-729" data-mp="1894k8exf3448">
            Send us a sample of recent bills and we'll show you exactly what
            Arissa's medical bill review could have saved.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-730" data-mp="fm1n9r8e31g3">
            <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold text-lg hover:bg-white transition-colors w-full sm:w-auto" data-id="element-731" data-mp="kjtiov88t5p1">
              Request a Free Bill Audit
            </Link>
            <a href="tel:8552601053" className="flex items-center justify-center text-white border border-white/30 px-8 py-4 rounded font-semibold text-lg hover:bg-white/10 transition-colors w-full sm:w-auto" data-id="element-732" data-mp="3szwo0ubvuia7">
              <Phone className="w-5 h-5 mr-2" data-id="element-733" data-mp="3gilusdy4mg8m" /> 855.260.1053
            </a>
          </div>
        </div>
      </section>
    </div>;
}